import type { InvitationInbox, InvitationInboxItem, InvitationInboxKind } from "@closer/shared";
import {
  ENTERPRISE_TENANT_TYPES,
  INVITATION_PURPOSE_LABELS,
  RECORD_ACCESS_LEVEL_NAMES,
  TENANT_TYPE_NAMES,
} from "@closer/shared";
import { Badge, Button, EmptyState, Input, ListRow, PageHeader, Select, Spinner } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { MailIcon } from "../../components/icons";
import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { resolveErrorState } from "../../lib/uiState";

/**
 * Davetlerim merkezi (CLOS-227 `[146]` → CLOS-1133).
 *
 * Hesap seviyesinde TEK merkez: raporun dört davet amacı (organizasyon üyeliği, müvekkil
 * ilişkisi, sözleşme tarafı, haricî misafir) beş kaynaktan gelir ve türüne göre AYRI
 * bölümlerde listelenir — türler tek listede rozetle ayrılsaydı "hangi davet ne açar?"
 * sorusu kullanıcıda kalırdı (INV-AT-12: doğru bağlam etiketi).
 *
 * ### Karar mantığı BURADA DEĞİL
 *
 * Kabul/Reddet düğmeleri yalnız merkez uçlarına POST atar; e-posta eşleşmesi, kapasite
 * ve uygunluk kontrolleri sunucudaki kanonik çekirdeklerde. Karar sonrası liste SUNUCUDAN
 * yeniden okunur — ekran sonucu uydurmaz, kanonik durumu yansıtır.
 *
 * ### ⚠ "Hangi organizasyon adına?" (K1)
 *
 * Sözleşme tarafı ve hizmet daveti bir ADRESE gelir ama karar bir ORGANİZASYON adına
 * verilir. Sunucu satırla birlikte kullanıcının adına yanıt verebileceği alanları da
 * gönderir (`acceptTargets`); ekran bir seçici çizer ve seçimi gövdede taşır. Liste
 * kolaylık, kapı sunucuda: seçilen alan orada yeniden üyelik + yetki kapısından geçer.
 * Uygun alan yoksa düğme yerine NEDEN söylenir — kesin reddedilecek eylem teklif edilmez.
 *
 * ### Süresi dolmuş / sonuçlanmış davet işlem TEKLİF ETMEZ
 *
 * `expired` TÜRETİLMİŞ gelir: satır DB'de hâlâ `pending` olabilir (tembel kapatma) ama
 * merkez süreyi sunucunun türettiği `displayStatus` ile gösterir.
 */
export type { InvitationInbox, InvitationInboxItem };

/** ProfileMenu rozeti AYNI anahtarı okur — iki yüzey tek sorgudan beslenir (kart kriteri). */
export const INVITATION_INBOX_QUERY_KEY = ["invitations", "inbox"] as const;

const DURUMLAR: Record<string, { etiket: string; tone: "neutral" | "success" | "danger" }> = {
  pending: { etiket: "Bekliyor", tone: "neutral" },
  accepted: { etiket: "Kabul edildi", tone: "success" },
  rejected: { etiket: "Reddedildi", tone: "danger" },
  cancelled: { etiket: "İptal edildi", tone: "neutral" },
  expired: { etiket: "Süresi doldu", tone: "danger" },
};

const BOLUMLER: {
  kind: InvitationInboxKind;
  baslik: string;
  aciklama: string;
}[] = [
  {
    kind: "organization",
    baslik: "Organizasyon davetleri",
    aciklama: "Kabul, sizi organizasyona üye yapar ve davetteki rolü verir.",
  },
  {
    kind: "contract_org",
    baslik: "Sözleşme tarafı davetleri",
    aciklama:
      "Kabul, seçtiğiniz organizasyonu sözleşmede ortak çalışmaya katar; erişim yalnız kabul edene açılır.",
  },
  {
    kind: "contract_participant",
    baslik: "Sözleşme katılımcısı davetleri",
    aciklama: "Kabul, yalnız ilgili sözleşme için erişim açar; üyelik oluşturmaz.",
  },
  {
    kind: "representative",
    baslik: "Müvekkil Temsilcisi davetleri",
    aciklama: "Kabul kaydedilir; erişim, sponsor organizasyon aktivasyonu tamamlayınca açılır.",
  },
  {
    kind: "service_relationship",
    baslik: "Hizmet ilişkisi davetleri",
    /**
     * ⚠ BÖLÜM AÇIKLAMASI YÖN İDDİA ETMİYOR (CLOS-1185 · canlı UAT'ta ölçüldü).
     *
     * Eski metin "Bir hukuk bürosu size hizmet vermeyi teklif ediyor" diyordu. Bu, iki
     * yönden yalnız BİRİ: sözleşmeden doğan davette teklif ters yönde akıyor ve satır
     * metni onu doğru anlatırken başlık aynı ekranda tersini söylüyordu. Aynı bölümde iki
     * yön birden bulunabildiği için tek bir yön cümlesi burada hiçbir zaman doğru olamaz;
     * yönü satır söylüyor (`baglam`), başlık yalnız ortak sonucu söylüyor.
     */
    aciklama:
      "Hukuki hizmet ilişkisi kurulmasını bekleyen davetler. Kimin kimden ne istediği ve kapsam her satırda yazıyor; kabul ilişkiyi başlatır, kayıt erişimi ayrıca verilir.",
  },
];

function tarih(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", { dateStyle: "medium" });
}

/** Türe göre bağlam satırı: kullanıcı NEYE davet edildiğini tek bakışta görür. */
function baglam(satir: InvitationInboxItem): string {
  switch (satir.kind) {
    case "organization":
      return satir.roleName ? `Rol: ${satir.roleName}` : "";
    case "contract_participant": {
      /** CLOS-1127: misafir yalnız görüntüler; erişim bitişi (K6) davet süresinden ayrı. */
      const bitis = satir.accessEndsAt
        ? ` · Erişim ${new Date(satir.accessEndsAt).toLocaleDateString("tr-TR", { dateStyle: "medium" })} tarihine kadar`
        : " · Erişim kaldırılana kadar";
      return `Sözleşme: ${satir.contractTitle ?? ""} · Görüntüleme (misafir)${bitis}`;
    }
    case "contract_org": {
      const erisim = satir.accessLevel ? ` · ${RECORD_ACCESS_LEVEL_NAMES[satir.accessLevel]}` : "";
      const kendi = satir.decisionContext === "self" ? " · Kendi adınıza yanıtlayacaksınız" : "";
      return `Sözleşme: ${satir.contractTitle ?? ""}${erisim}${kendi}`;
    }
    case "representative":
      return `Müvekkil: ${satir.clientDisplayName ?? ""}`;
    case "service_relationship":
      /**
       * ⚠ İKİ YÖN, İKİ CÜMLE (CLOS-1185). Bölüm başlığı türe göre yazılıyor ve büro
       * teklifini anlatıyor; sözleşmeden başlayan davette yön TERS — sağlayıcıya "bir büro
       * size hizmet teklif ediyor" demek, kimin kime ne teklif ettiğini yanlış anlatırdı.
       * Canlı UAT'ta ölçüldü: ekran sağlayıcıya yanlış hikâyeyi anlatıyordu.
       */
      if (satir.serviceDirection === "contract_origin_request") {
        return `Bir müvekkil sözleşmesinde sizden hukuki hizmet talep ediyor · Kapsam: ${
          satir.scopeLabel ?? ""
        }`;
      }
      /** CLOS-1122: gerçek kişi müvekkil daveti KENDİ ADINA yanıtlanır — seçici yok, cümle var. */
      return `Kapsam: ${satir.scopeLabel ?? ""}${
        satir.decisionContext === "self" ? " · Kendi adınıza yanıtlayacaksınız" : ""
      }`;
  }
}

export function MyInvitationsPage() {
  const queryClient = useQueryClient();
  const [hata, setHata] = useState<string | undefined>();
  /** Satır başına seçilen bağlam — yalnız bağlam isteyen türlerde okunur. */
  const [secimler, setSecimler] = useState<Record<string, string>>({});
  /** "Kurum oluştur ve kabul et" formu: satır başına ad + tür (tür seçilmeden gönderilmez). */
  const [yeniKurum, setYeniKurum] = useState<Record<string, { name: string; type: string }>>({});

  const sorgu = useQuery({
    queryKey: INVITATION_INBOX_QUERY_KEY,
    queryFn: async () => (await api<InvitationInbox>("/api/invitations/inbox")).data,
  });
  const { data, isLoading } = sorgu;

  const karar = useMutation({
    mutationFn: async (girdi: {
      kind: string;
      id: string;
      islem: "accept" | "reject";
      tenantId?: string;
      /** CLOS-1125 / Halit S2: kurum oluştur + kabul, tek istek (atomik). */
      createOrganization?: { name: string; type: string };
    }) => {
      const govde = girdi.createOrganization
        ? { createOrganization: girdi.createOrganization }
        : girdi.tenantId
          ? { tenantId: girdi.tenantId }
          : null;
      await api(`/api/invitations/inbox/${girdi.kind}/${girdi.id}/${girdi.islem}`, {
        method: "POST",
        ...(govde ? { body: JSON.stringify(govde) } : {}),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: INVITATION_INBOX_QUERY_KEY });
    },
    onError: (err: unknown) => {
      setHata(err instanceof Error ? err.message : "İşlem tamamlanamadı");
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const hepsi = data?.items ?? [];

  return (
    <section className="flex w-full flex-col gap-6">
      <PageHeader
        title="Davetlerim"
        description="Farklı organizasyonlardan ve çalışma alanlarından size gelen davetler."
      />

      {hata ? (
        <p role="alert" className="text-sm text-danger">
          {hata}
        </p>
      ) : null}

      {/*
        ⚠ ARIZA "DAVETİM YOK" DEĞİL (kusur avı, 26 Ağustos). Sorgu hata verdiğinde ekran
        "Bekleyen davetiniz yok" diyordu: kullanıcı e-postasındaki daveti gördüğü hâlde
        merkezde bulamıyor ve davetin geri çekildiğini sanıyordu.
      */}
      {sorgu.error ? (
        <StateView
          state={resolveErrorState(sorgu.error, {
            idempotent: true,
            existenceIsSecret: false,
            overrides: {
              error: {
                title: "Davetleriniz yüklenemedi",
                description: "Bağlantı sorunları geçici olabilir.",
              },
            },
          })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : hepsi.length === 0 ? (
        <EmptyState
          title="Bekleyen davetiniz yok"
          description="Size gönderilen davetler burada görünecek."
        />
      ) : (
        BOLUMLER.map((bolum) => {
          const satirlar = hepsi.filter((s) => s.kind === bolum.kind);
          if (satirlar.length === 0) return null;
          return (
            <div
              key={bolum.kind}
              className="flex flex-col gap-2"
              data-testid={`bolum-${bolum.kind}`}
            >
              <div className="flex flex-col gap-0.5">
                <h2 className="text-sm font-medium text-fg-strong">{bolum.baslik}</h2>
                <p className="text-xs text-fg-muted">{bolum.aciklama}</p>
              </div>
              {satirlar.map((satir) => {
                const durum = DURUMLAR[satir.displayStatus] ?? {
                  etiket: satir.displayStatus,
                  tone: "neutral" as const,
                };
                const bekliyor = satir.displayStatus === "pending";
                /** Yalnız açık iki bağlam türü seçim ister; tanımsız/`null` kişi davetidir. */
                const baglamGerekli =
                  satir.decisionContext === "organization" ||
                  satir.decisionContext === "client_area";
                const hedefler = satir.acceptTargets ?? [];
                /** Tek uygun alan varsa seçici zaten onu gösterir; seçim o alan sayılır. */
                const secili = secimler[satir.id] ?? hedefler[0]?.tenantId;
                const yanitlanabilir = bekliyor && (!baglamGerekli || Boolean(secili));
                const gonder = (islem: "accept" | "reject") => {
                  setHata(undefined);
                  karar.mutate({
                    kind: satir.kind,
                    id: satir.id,
                    islem,
                    ...(baglamGerekli ? { tenantId: secili } : {}),
                  });
                };
                return (
                  <ListRow
                    key={satir.id}
                    className="!h-auto !items-start !py-2.5"
                    icon={<MailIcon />}
                    title={satir.organizationName}
                    subtitle={
                      <>
                        {baglam(satir)} · {INVITATION_PURPOSE_LABELS[satir.purpose]} · Son
                        geçerlilik: {tarih(satir.expiresAt)}
                        {bekliyor && baglamGerekli ? (
                          <span className="mt-1 block">
                            {hedefler.length > 0 ? (
                              <label className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
                                {satir.decisionContext === "client_area"
                                  ? "Hangi alan adına:"
                                  : "Hangi organizasyon adına:"}
                                <Select
                                  aria-label="Yanıt verilecek organizasyon"
                                  value={secili ?? ""}
                                  onChange={(e) =>
                                    setSecimler((s) => ({ ...s, [satir.id]: e.target.value }))
                                  }
                                >
                                  {hedefler.map((h) => (
                                    <option key={h.tenantId} value={h.tenantId}>
                                      {h.name}
                                    </option>
                                  ))}
                                </Select>
                              </label>
                            ) : satir.kind === "contract_org" && satir.canCreateOrganization ? (
                              /*
                                ⚠ KURUM OLUŞTUR VE KABUL ET (CLOS-1125 / Halit S2): kullanıcının
                                yetkili kurumu yok; onboarding'e gönderip geri getirmek yerine
                                kurum + kurucu yöneticilik + kabul tek adımda. Tür AÇIKÇA seçilir
                                (varsayılan yok — hukuki taraf hukuk bürosu da olabilir); ad
                                davetteki taraf unvanıyla ön dolu. Domain/ad ile eşleşme YOK.
                              */
                              <span
                                className="flex flex-wrap items-end gap-2"
                                data-testid={`kurum-olustur-${satir.id}`}
                              >
                                <label className="flex flex-col gap-1 text-xs text-fg-muted">
                                  Kurum adı
                                  <Input
                                    aria-label="Kurum adı"
                                    value={
                                      yeniKurum[satir.id]?.name ??
                                      satir.suggestedOrganizationName ??
                                      ""
                                    }
                                    onChange={(e) =>
                                      setYeniKurum((k) => ({
                                        ...k,
                                        [satir.id]: {
                                          name: e.target.value,
                                          type: k[satir.id]?.type ?? "",
                                        },
                                      }))
                                    }
                                  />
                                </label>
                                <label className="flex flex-col gap-1 text-xs text-fg-muted">
                                  Kurum türü
                                  <Select
                                    aria-label="Kurum türü"
                                    value={yeniKurum[satir.id]?.type ?? ""}
                                    onChange={(e) =>
                                      setYeniKurum((k) => ({
                                        ...k,
                                        [satir.id]: {
                                          name:
                                            k[satir.id]?.name ??
                                            satir.suggestedOrganizationName ??
                                            "",
                                          type: e.target.value,
                                        },
                                      }))
                                    }
                                  >
                                    <option value="">Seçiniz</option>
                                    {ENTERPRISE_TENANT_TYPES.map((t) => (
                                      <option key={t} value={t}>
                                        {TENANT_TYPE_NAMES[t]}
                                      </option>
                                    ))}
                                  </Select>
                                </label>
                                <Button
                                  size="sm"
                                  disabled={
                                    karar.isPending ||
                                    (yeniKurum[satir.id]?.type ?? "") === "" ||
                                    (
                                      yeniKurum[satir.id]?.name ??
                                      satir.suggestedOrganizationName ??
                                      ""
                                    ).trim().length < 2
                                  }
                                  onClick={() => {
                                    setHata(undefined);
                                    karar.mutate({
                                      kind: satir.kind,
                                      id: satir.id,
                                      islem: "accept",
                                      createOrganization: {
                                        name: (
                                          yeniKurum[satir.id]?.name ??
                                          satir.suggestedOrganizationName ??
                                          ""
                                        ).trim(),
                                        type: yeniKurum[satir.id]!.type,
                                      },
                                    });
                                  }}
                                >
                                  Kurum oluştur ve kabul et
                                </Button>
                                {/*
                                  CLOS-1128 (Faz 6 UX borcu): kurumu Closer'da olan ama daveti
                                  kabul yetkisi olmayan üye yalnız "kurum oluştur" görüyordu;
                                  ikinci kurum kurmasın diye doğru yol söyleniyor.
                                */}
                                <span
                                  className="basis-full text-xs text-fg-soft"
                                  data-testid={`kurum-olustur-ipucu-${satir.id}`}
                                >
                                  Kurumunuz zaten Closer'daysa yeni kurum kurmayın: kurumunuzda
                                  sözleşme paylaşma yetkisi olan bir yönetici bu daveti kendi
                                  hesabından kabul edebilir.
                                </span>
                              </span>
                            ) : (
                              /*
                                Kesin reddedilecek eylem teklif edilmez; NEDEN söylenir.

                                ⚠ KURUM VAR, YETKİ YOK (CLOS-1128 · Halit K3): kullanıcının
                                kurumsal üyeliği varsa sunucu "kurum oluştur"u SUNMAZ
                                (`canCreateOrganization=false`); ana çözüm yetkili bir
                                meslektaşın kabulü. Yeni kurum kurma yalnız kurumsal bağlamı
                                olmayan kullanıcıya, ikincil onboarding yolu olarak açılır.
                              */
                              <span className="text-xs text-warning" data-testid="baglam-yok">
                                {satir.decisionContext === "client_area"
                                  ? "Bu daveti yanıtlayabileceğiniz bir Müvekkil Alanı veya organizasyon yok."
                                  : "Bu daveti kurumunuzda sözleşme paylaşma yetkisi bulunan bir kullanıcı kabul edebilir."}
                              </span>
                            )}
                          </span>
                        ) : null}
                      </>
                    }
                    trailing={
                      <div className="flex items-center gap-2">
                        <Badge tone={durum.tone}>{durum.etiket}</Badge>
                        {yanitlanabilir ? (
                          <>
                            <Button
                              size="sm"
                              disabled={karar.isPending}
                              onClick={() => gonder("accept")}
                            >
                              Kabul Et
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={karar.isPending}
                              onClick={() => gonder("reject")}
                            >
                              Reddet
                            </Button>
                          </>
                        ) : null}
                      </div>
                    }
                  />
                );
              })}
            </div>
          );
        })
      )}
    </section>
  );
}
